import React, { useState, useEffect, useRef } from 'react'
import { CircularProgress, Drawer, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import moment from 'moment'
import { fetchSyncLifecycleStatus, SyncLifecycleStatusWithDurations } from 'api/reporting/syncLifecycles'
import { ReportingOrdersPlatformName } from 'types'
import { formatDurationSeconds } from 'utils'
import {
  DrawerHeader,
  DrawerContent,
  DrawerRow,
  DrawerRowPair,
  DrawerLabel,
  DrawerValue,
  StatusChip,
  PlatformStatusText,
  PlatformMetaText,
} from './styles'

interface MenuImportHistoryDrawerProps {
  isOpen: boolean
  onClose: () => void
  syncLifecycleId: string
}

const MenuImportHistoryDrawer: React.FC<MenuImportHistoryDrawerProps> = ({
  isOpen,
  onClose,
  syncLifecycleId,
}) => {
  const [menuImport, setMenuImport] = useState<SyncLifecycleStatusWithDurations | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const lastFetchedId = useRef<string | null>(null)

  useEffect(() => {
    if (!isOpen || !syncLifecycleId || lastFetchedId.current === syncLifecycleId) return

    const fetchData = async () => {
      setIsLoading(true)
      lastFetchedId.current = syncLifecycleId
      try {
        const details = await fetchSyncLifecycleStatus(syncLifecycleId)
        setMenuImport(details)
      } catch (err) {
        console.error('Failed to fetch sync lifecycle details', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [isOpen, syncLifecycleId])

  useEffect(() => {
    if (!isOpen) {
      lastFetchedId.current = null
      setMenuImport(null)
    }
  }, [isOpen])
  
  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { maxWidth: '410px', width: '100%' } }}
    >
      <DrawerHeader>
        <h2>Menu Import Details</h2>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DrawerHeader>

      <DrawerContent>
        {isLoading ? (
          <CircularProgress />
        ) : menuImport ? (
          <>
            <DrawerRowPair>
              <DrawerRow>
                <DrawerLabel>Status</DrawerLabel>
                <StatusChip
                  label={menuImport.aasm_state}
                  status={menuImport.aasm_state}
                  size='small'
                />
              </DrawerRow>
              <DrawerRow>
                <DrawerLabel>Sync Record ID</DrawerLabel>
                <DrawerValue>{menuImport.id ?? '—'}</DrawerValue>
              </DrawerRow>
            </DrawerRowPair>

            <DrawerRow full>
              <DrawerLabel>Import Date & Time</DrawerLabel>
              <DrawerValue>
                {menuImport.import_requested_at
                  ? moment(menuImport.import_requested_at).format('dddd, MM/DD/YYYY, h:mm A')
                  : '—'}
              </DrawerValue>
            </DrawerRow>

            <DrawerRowPair>
              <DrawerRow>
                <DrawerLabel>Initial Import Duration</DrawerLabel>
                <DrawerValue>{formatDurationSeconds(menuImport.initialImportDuration)}</DrawerValue>
              </DrawerRow>
              <DrawerRow>
                <DrawerLabel>Ingest Duration</DrawerLabel>
                <DrawerValue> - </DrawerValue>
              </DrawerRow>
            </DrawerRowPair>

            <DrawerRowPair>
              <DrawerRow>
                <DrawerLabel>Publish Duration</DrawerLabel>
                <DrawerValue>{formatDurationSeconds(menuImport.publishDuration)}</DrawerValue>
              </DrawerRow>
              <DrawerRow>
                <DrawerLabel>TOOS Processing Duration</DrawerLabel>
                <DrawerValue>{formatDurationSeconds(menuImport.toosProcessingDuration)}</DrawerValue>
              </DrawerRow>
            </DrawerRowPair>

            <DrawerRowPair>
              <DrawerRow>
                <DrawerLabel>{ReportingOrdersPlatformName.CHOWLY_ONLINE_ORDERING}</DrawerLabel>
                <DrawerValue>
                  <PlatformStatusText success={menuImport.publishMenuStatuses[ReportingOrdersPlatformName.CHOWLY_ONLINE_ORDERING]?.success}>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.CHOWLY_ONLINE_ORDERING]?.success ? 'Published' : 'Unpublished'}
                  </PlatformStatusText>
                  <PlatformMetaText>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.CHOWLY_ONLINE_ORDERING]?.external_processing_completed_at
                      ? moment(menuImport.publishMenuStatuses[ReportingOrdersPlatformName.CHOWLY_ONLINE_ORDERING].external_processing_completed_at).format('MM/DD/YY, h:mm A')
                      : '—'}
                  </PlatformMetaText>
                </DrawerValue>
              </DrawerRow>
              <DrawerRow>
                <DrawerLabel>{ReportingOrdersPlatformName.DOORDASH}</DrawerLabel>
                <DrawerValue>
                  <PlatformStatusText success={menuImport.publishMenuStatuses[ReportingOrdersPlatformName.DOORDASH]?.success}>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.DOORDASH]?.success ? 'Published' : 'Unpublished'}
                  </PlatformStatusText>
                  <PlatformMetaText>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.DOORDASH]?.external_processing_completed_at
                      ? moment(menuImport.publishMenuStatuses[ReportingOrdersPlatformName.DOORDASH].external_processing_completed_at).format('MM/DD/YY, h:mm A')
                      : '—'}
                  </PlatformMetaText>
                </DrawerValue>
              </DrawerRow>
            </DrawerRowPair>

            <DrawerRowPair>
              <DrawerRow>
                <DrawerLabel>{ReportingOrdersPlatformName.UBEREATS}</DrawerLabel>
                <DrawerValue>
                  <PlatformStatusText success={menuImport.publishMenuStatuses[ReportingOrdersPlatformName.UBEREATS]?.success}>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.UBEREATS]?.success ? 'Published' : 'Unpublished'}
                  </PlatformStatusText>
                  <PlatformMetaText>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.UBEREATS]?.external_processing_completed_at
                      ? moment(menuImport.publishMenuStatuses[ReportingOrdersPlatformName.UBEREATS].external_processing_completed_at).format('MM/DD/YY, h:mm A')
                      : '—'}
                  </PlatformMetaText>
                </DrawerValue>
              </DrawerRow>
              <DrawerRow>
                <DrawerLabel>{ReportingOrdersPlatformName.GRUBHUB}</DrawerLabel>
                <DrawerValue>
                  <PlatformStatusText success={menuImport.publishMenuStatuses[ReportingOrdersPlatformName.GRUBHUB]?.success}>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.GRUBHUB]?.success ? 'Published' : 'Unpublished'}
                  </PlatformStatusText>
                  <PlatformMetaText>
                    {menuImport.publishMenuStatuses[ReportingOrdersPlatformName.GRUBHUB]?.external_processing_completed_at
                      ? moment(menuImport.publishMenuStatuses[ReportingOrdersPlatformName.GRUBHUB].external_processing_completed_at).format('MM/DD/YY, h:mm A')
                      : '—'}
                  </PlatformMetaText>
                </DrawerValue>
              </DrawerRow>
            </DrawerRowPair>
          </>
        ) : (
          <div>No details available.</div>
        )}
      </DrawerContent>
    </Drawer>
  )
}

export default MenuImportHistoryDrawer
